import React, { useEffect, useState } from 'react'
import { View, ViewStyle, TextStyle, TouchableOpacity, Switch, Modal } from 'react-native'
import {
  Header,
  color,
  Text,
  spacing,
  Button,
  SvgIcon,
  iconChevronRight,
  iconArrowBack,
} from '@trivie/ui-mobile'
import { DateTime } from 'luxon'
import { useStores } from '@trivie/core'
import { useHistory } from 'react-router'

const modalButtonStyling: ViewStyle = {
  paddingVertical: spacing[3],
  borderTopWidth: 1,
  borderTopColor: color.shade20,
}

export const NotificationsSettingsScreen = () => {
  const { userStore } = useStores()
  const { user } = userStore
  const [isPaused, setIsPaused] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const toggleModal = (value: boolean) => {
    if (value) {
      setIsPaused(true)
      setShowModal(true)
    } else {
      userStore.setUserCommSettings({ push_notifications_allowed: null })
      setIsPaused(false)
    }
  }
  const history = useHistory()

  const goBack = () => history.goBack()
  const goToPostsCommentsScreen = () => history.push('/settings/notifications/talk')
  const goToQuizzesTestsScreen = () => history.push('/settings/notifications/quizzes')
  const goToEmailScreen = () => history.push('/settings/notifications/emails')
  const onPausePress = (value: any) => {
    let pauseTime = DateTime.local()

    if (value === '1 hour') {
      pauseTime = pauseTime.plus({ hour: 1 })
    } else if (value === '8 hours') {
      pauseTime = pauseTime.plus({ hour: 8 })
    } else if (value === '1 day') {
      pauseTime = pauseTime.plus({ day: 1 })
    } else if (value === '3 days') {
      pauseTime = pauseTime.plus({ days: 3 })
    } else if (value === '1 week') {
      pauseTime = pauseTime.plus({ week: 1 })
    }

    userStore.setUserCommSettings({ push_notifications_allowed: pauseTime.toISO() })
    setShowModal(false)
  }

  if (!user) {
    return null
  }

  useEffect(() => {
    if (user?.user_comm_settings?.push_notifications_allowed) {
      const pauseTime = DateTime.fromISO(user?.user_comm_settings.push_notifications_allowed)
      if (pauseTime > DateTime.local()) {
        setIsPaused(true)
      }
    }
  }, [])

  return (
    <View testID="NotificationsSettingsScreen" style={FULL}>
      <div style={CONTAINER}>
        <Header
          leftIcon={iconArrowBack}
          onLeftPress={goBack}
          center
          style={HEADER}
          titleStyle={HEADER_TITLE}
          headerTx="Notifications.header"
        />

        <Text variant="titleEmphasized" tx="Settings.Notifications.push" />

        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomColor: 'rgba(0, 0, 0, 0.1)',
            alignItems: 'center',
            paddingVertical: spacing[4],
          }}
        >
          <Text variant="label" tx="Settings.Notifications.pause" />
          <Switch
            // @ts-ignore
            dataSet={{ qat: `pause_notifications_toggle` }}
            accessible
            accessibilityRole="switch"
            accessibilityHint="Toggle Pause All Notifications"
            accessibilityLabel="Pause All: true/false"
            trackColor={{ true: color.green, false: color.darkenGrey }}
            thumbColor="white"
            value={isPaused}
            onValueChange={(value) => toggleModal(value)}
          />
        </View>

        {userStore.user?.workspace_settings?.feed_enabled && (
          <View
            style={{
              borderBottomWidth: 1,
              borderBottomColor: 'rgba(0, 0, 0, 0.1)',
            }}
          >
            <TouchableOpacity
              accessibilityRole="link"
              accessibilityLabel="Go to post comments"
              onPress={() => {
                goToPostsCommentsScreen()
              }}
              style={{ flexDirection: 'row' }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  flex: 1,
                  justifyContent: 'space-between',
                  alignContent: 'center',
                  paddingVertical: spacing[4],
                }}
              >
                <Text variant="label" tx="Settings.Notifications.postsComments" />
                <SvgIcon src={iconChevronRight} color={color.shade30} size={24} />
              </View>
            </TouchableOpacity>
          </View>
        )}
        <View
          style={{
            borderBottomWidth: 1,
            borderBottomColor: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          <TouchableOpacity
            accessibilityRole="link"
            accessibilityLabel="Go to quizzes"
            onPress={() => {
              goToQuizzesTestsScreen()
            }}
            style={{ flexDirection: 'row' }}
          >
            <View
              style={{
                flexDirection: 'row',
                flex: 1,
                justifyContent: 'space-between',
                alignContent: 'center',
                paddingVertical: spacing[4],
              }}
            >
              <Text variant="label" tx="Settings.Notifications.quizTest" />
              <SvgIcon src={iconChevronRight} color={color.shade30} size={24} />
            </View>
          </TouchableOpacity>
        </View>
        <TouchableOpacity
          accessibilityRole="link"
          accessibilityLabel="Go to email"
          onPress={() => {
            goToEmailScreen()
          }}
          style={{ flexDirection: 'row' }}
        >
          <View
            style={{
              flexDirection: 'row',
              flex: 1,
              justifyContent: 'space-between',
              alignContent: 'center',
              paddingVertical: spacing[4],
            }}
          >
            <Text variant="label" tx="Onboarding.email" />
            <SvgIcon src={iconChevronRight} color={color.shade30} size={24} />
          </View>
        </TouchableOpacity>
      </div>

      <Modal visible={showModal} transparent>
        <View
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            height: '100%',
            justifyContent: 'flex-end',
            paddingBottom: spacing[6],
            paddingHorizontal: spacing[2],
          }}
        >
          <View
            style={{
              backgroundColor: 'rgba(237, 237, 237, 1)',
              borderRadius: 14,
              marginBottom: spacing[2],
            }}
          >
            <Text
              variant="body1Emphasized"
              tx="Settings.Notifications.pauseModal"
              txOptions={{ Trivie: 'Trivie' }}
              style={{
                fontSize: 13,
                marginVertical: spacing[3],
                marginHorizontal: spacing[5],
                color: '#9098A3',
              }}
            />

            <View style={{ ...modalButtonStyling }}>
              <TouchableOpacity
                accessibilityRole="switch"
                accessibilityLabel="Pause for 1 hour"
                onPress={() => onPausePress('1 hour')}
              >
                <Text
                  tx="Settings.Notifications.oneHour"
                  style={{ textAlign: 'center', fontWeight: '400', fontSize: 20, lineHeight: 24 }}
                />
              </TouchableOpacity>
            </View>

            <View style={{ ...modalButtonStyling }}>
              <TouchableOpacity
                accessibilityRole="switch"
                accessibilityLabel="Pause for 8 hour"
                onPress={() => onPausePress('8 hours')}
              >
                <Text
                  tx="Settings.Notifications.eightHours"
                  style={{ textAlign: 'center', fontSize: 20, lineHeight: 24 }}
                />
              </TouchableOpacity>
            </View>

            <View style={{ ...modalButtonStyling }}>
              <TouchableOpacity
                accessibilityRole="switch"
                accessibilityLabel="Pause for 1 day"
                onPress={() => onPausePress('1 day')}
              >
                <Text
                  tx="Settings.Notifications.oneDay"
                  style={{ textAlign: 'center', fontWeight: '400', fontSize: 20, lineHeight: 24 }}
                />
              </TouchableOpacity>
            </View>

            <View style={{ ...modalButtonStyling }}>
              <TouchableOpacity
                accessibilityRole="switch"
                accessibilityLabel="Pause for 3 days"
                onPress={() => onPausePress('3 days')}
              >
                <Text
                  tx="Settings.Notifications.threeDays"
                  style={{ textAlign: 'center', fontSize: 20, fontWeight: '400', lineHeight: 24 }}
                />
              </TouchableOpacity>
            </View>

            <View style={{ ...modalButtonStyling }}>
              <TouchableOpacity
                accessibilityRole="switch"
                accessibilityLabel="Pause for 1 week"
                onPress={() => onPausePress('1 week')}
              >
                <Text
                  tx="Settings.Notifications.oneWeek"
                  style={{ fontWeight: '400', textAlign: 'center', fontSize: 20, lineHeight: 24 }}
                />
              </TouchableOpacity>
            </View>
          </View>
          <View>
            <Button
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              onPress={() => {
                setIsPaused(!isPaused)
                setShowModal(false)
              }}
              variant="outlined"
              tx="Common.cancel"
            />
          </View>
        </View>
      </Modal>
    </View>
  )
}

/**
 * Styling
 */
const FULL: ViewStyle = { flex: 1 }
const CONTAINER: React.CSSProperties = {
  backgroundColor: color.white,
}
const HEADER_TITLE: TextStyle = { fontSize: 24, color: color.black }
const HEADER: ViewStyle = { marginBottom: 20 }
